import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Task } from '../types';

interface Props {
  task: Task;
  onComplete: (id: string, completed: boolean) => void;
  onDelete: (id: string) => void;
  onDueDateChange: (id: string, date: string | undefined) => void;
  onProjectChange: (id: string, project: string | undefined) => void;
  isDragOverlay?: boolean;
}

const PRIORITY_STYLES: Record<Task['priority'], string> = {
  urgent: 'bg-red-900/50 text-red-300 border border-red-700/50',
  normal: 'bg-amber-900/40 text-amber-300 border border-amber-700/40',
  low: 'bg-gray-800 text-gray-400 border border-gray-700',
};

const PRIORITY_LABELS: Record<Task['priority'], string> = {
  urgent: '⚡ Urgent',
  normal: '● Normal',
  low: '○ Low',
};

const SOURCE_STYLES: Record<Task['source'], string> = {
  gmail: 'bg-blue-900/40 text-blue-300 border border-blue-700/40',
  slack: 'bg-purple-900/40 text-purple-300 border border-purple-700/40',
  calls: 'bg-green-900/40 text-green-300 border border-green-700/40',
  manual: 'bg-gray-800 text-gray-400 border border-gray-700',
};

const SOURCE_LABELS: Record<Task['source'], string> = {
  gmail: '✉ Gmail',
  slack: '# Slack',
  calls: '◎ Calls',
  manual: '✦ Manual',
};

function dueDateStyle(dueDate?: string): { color: string; label: string } | null {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((due.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return { color: 'text-red-400', label: `Overdue ${formatDate(dueDate)}` };
  if (diffDays === 0) return { color: 'text-orange-400', label: 'Due today' };
  if (diffDays === 1) return { color: 'text-yellow-400', label: 'Due tomorrow' };
  if (diffDays <= 3) return { color: 'text-yellow-300', label: `Due ${formatDate(dueDate)}` };
  return { color: 'text-gray-400', label: `Due ${formatDate(dueDate)}` };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function TaskCard({
  task,
  onComplete,
  onDelete,
  onDueDateChange,
  onProjectChange,
  isDragOverlay = false,
}: Props) {
  const [editingDate, setEditingDate] = useState(false);
  const [editingProject, setEditingProject] = useState(false);
  const [projectValue, setProjectValue] = useState(task.project ?? '');
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const due = dueDateStyle(task.dueDate);
  const wasEdited =
    new Date(task.updatedAt).getTime() - new Date(task.createdAt).getTime() > 300_000;

  function handleProjectCommit() {
    const trimmed = projectValue.trim();
    onProjectChange(task.id, trimmed || undefined);
    setEditingProject(false);
  }

  const hasTooltip = !!task.notes && !isDragOverlay;

  return (
    <div
      className={`group bg-gray-900 border rounded-lg p-3 transition ${
        isDragOverlay
          ? 'border-indigo-500 shadow-2xl shadow-indigo-900/50 rotate-1'
          : 'border-gray-800 hover:border-gray-700'
      } ${task.status === 'completed' ? 'opacity-60' : ''}`}
      onMouseMove={hasTooltip ? (e) => setTooltipPos({ x: e.clientX, y: e.clientY }) : undefined}
      onMouseLeave={hasTooltip ? () => setTooltipPos(null) : undefined}
    >
      <div className="flex items-start gap-2">
        {/* Checkbox */}
        <button
          onClick={(e) => { e.stopPropagation(); onComplete(task.id, !task.completed); }}
          className={`mt-0.5 w-4 h-4 shrink-0 rounded border transition ${
            task.completed
              ? 'bg-indigo-600 border-indigo-600'
              : 'border-gray-600 hover:border-indigo-500'
          } flex items-center justify-center`}
        >
          {task.completed && (
            <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
              <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>

        {/* Title */}
        <p className={`text-sm font-medium flex-1 min-w-0 ${task.completed ? 'line-through text-gray-500' : 'text-gray-100'}`}>
          {task.title}
        </p>

        {/* Edited indicator */}
        {wasEdited && !isDragOverlay && (
          <span
            className="text-gray-700 text-xs shrink-0 mt-0.5"
            title={`Edited ${new Date(task.updatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
          >
            ✎
          </span>
        )}

        {/* Delete */}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
          className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 transition text-lg leading-none shrink-0"
          title="Delete task"
        >
          ×
        </button>
      </div>

      {task.notes && (
        <p className="text-xs text-gray-500 mt-1 ml-8 truncate">{task.notes}</p>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mt-2 ml-8">
        {/* Priority */}
        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${PRIORITY_STYLES[task.priority]}`}>
          {PRIORITY_LABELS[task.priority]}
        </span>

        {/* Source */}
        <span className={`text-xs px-1.5 py-0.5 rounded ${SOURCE_STYLES[task.source]}`}>
          {task.sourceLink ? (
            <a
              href={task.sourceLink}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {SOURCE_LABELS[task.source]}
            </a>
          ) : SOURCE_LABELS[task.source]}
        </span>

        {/* Due date */}
        {editingDate ? (
          <input
            type="date"
            defaultValue={task.dueDate?.slice(0, 10) ?? ''}
            autoFocus
            className="text-xs bg-gray-800 border border-indigo-500 rounded px-1 py-0.5 text-gray-200 focus:outline-none"
            onChange={(e) => {
              onDueDateChange(task.id, e.target.value || undefined);
              setEditingDate(false);
            }}
            onBlur={() => setEditingDate(false)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : due ? (
          <span
            className={`text-xs cursor-pointer ${due.color} hover:opacity-80`}
            onClick={(e) => { e.stopPropagation(); setEditingDate(true); }}
            title="Click to edit due date"
          >
            ◷ {due.label}
          </span>
        ) : (
          <span
            className="text-xs text-gray-700 hover:text-gray-500 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); setEditingDate(true); }}
            title="Add due date"
          >
            + due
          </span>
        )}

        {/* Project tag */}
        {editingProject ? (
          <input
            type="text"
            value={projectValue}
            autoFocus
            className="text-xs bg-gray-800 border border-indigo-500 rounded px-1 py-0.5 text-gray-200 focus:outline-none w-24"
            onChange={(e) => setProjectValue(e.target.value)}
            onBlur={handleProjectCommit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleProjectCommit();
              if (e.key === 'Escape') setEditingProject(false);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : task.project ? (
          <span
            className="text-xs bg-indigo-900/40 text-indigo-300 border border-indigo-700/40 px-1.5 py-0.5 rounded cursor-pointer hover:bg-indigo-900/60"
            onClick={(e) => { e.stopPropagation(); setProjectValue(task.project ?? ''); setEditingProject(true); }}
            title="Click to edit project"
          >
            ⌁ {task.project}
          </span>
        ) : (
          <span
            className="text-xs text-gray-700 hover:text-gray-500 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); setProjectValue(''); setEditingProject(true); }}
            title="Add project"
          >
            + project
          </span>
        )}
      </div>

      {/* Hover tooltip for full context — rendered at body level to escape overflow clipping */}
      {hasTooltip && tooltipPos &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              left: tooltipPos.x + 14,
              top: tooltipPos.y + 14,
              zIndex: 9999,
              maxWidth: 280,
              pointerEvents: 'none',
            }}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 shadow-xl"
          >
            <p className="text-xs text-gray-300 leading-relaxed line-clamp-3">{task.notes}</p>
          </div>,
          document.body,
        )}
    </div>
  );
}
